/**
 * Route guards for authenticated / admin-only pages.
 *
 * <ProtectedAdmin> — used to wrap the entire /admin subtree.
 *   • While AuthContext is still loading -> shows a full-screen spinner.
 *   • If no user or role !== "admin" -> redirects to /login?redirect=<pathname>.
 *   • If user is admin -> renders children (via <Outlet />).
 */
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ShieldAlert } from "lucide-react";

function FullscreenSpinner({ label = "Checking your access…" }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0B0B0D] text-white" data-testid="auth-guard-spinner">
            <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" />
                <p className="text-xs font-mono-ui tracking-[0.18em] text-white/60 uppercase">{label}</p>
            </div>
        </div>
    );
}

export function ProtectedAdmin() {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) return <FullscreenSpinner label="Verifying admin session…" />;

    if (!user) {
        // Not signed in at all — redirect to login and remember where they were going
        const redirect = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/login?redirect=${redirect}&reason=admin`} replace />;
    }

    if (user.role !== "admin") {
        // Signed in but not an admin — show a clean 403 page (no admin shell exposed)
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0B0B0D] text-white px-6" data-testid="auth-guard-forbidden">
                <div className="max-w-md text-center border border-white/10 rounded-3xl p-10 bg-white/5">
                    <ShieldAlert className="h-8 w-8 mx-auto text-[hsl(var(--accent))]" />
                    <h1 className="mt-4 text-2xl font-display">Restricted area</h1>
                    <p className="mt-2 text-sm text-white/60">
                        Your account does not have permission to view X-Plosiv Studio.
                    </p>
                    <a
                        href="/"
                        className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[hsl(var(--accent))] text-white text-xs font-mono-ui tracking-[0.18em] uppercase hover:brightness-110"
                        data-testid="auth-guard-back-home"
                    >
                        Back to storefront
                    </a>
                </div>
            </div>
        );
    }

    return <Outlet />;
}

export function ProtectedUser() {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) return <FullscreenSpinner label="Loading your account…" />;
    if (!user) {
        const redirect = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/login?redirect=${redirect}`} replace />;
    }
    return <Outlet />;
}
